import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Collaborator } from "../hooks/useCollaborationClient";
import { publishDraftVersion, updateFormMetadata, createFormShareRequest, listFormShares } from "../lib/api-client";
import { useBuilderStore } from "../hooks/useBuilderStore";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, type ThemeMode } from "../contexts/ThemeContext";
import type { FormVersionDetail } from "../types/api";
import { validateFormSchema } from "@rwaforms/shared";

function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return "Not saved yet";
  const date = new Date(timestamp);
  return `Saved ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

interface BuilderTopBarProps {
  collaborators?: Collaborator[];
  connectionStatus?: "idle" | "connecting" | "connected" | "disconnected";
}

function BuilderTopBar({ collaborators = [], connectionStatus = "idle" }: BuilderTopBarProps): JSX.Element {
  const { teamId, formId } = useParams<{ teamId: string; formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<FormVersionDetail | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const formTitle = useBuilderStore((state) => state.formTitle);
  const formDescription = useBuilderStore((state) => state.formDescription ?? "");
  const setFormTitle = useBuilderStore((state) => state.setFormTitle);
  const setFormDescription = useBuilderStore((state) => state.setFormDescription);
  const draftVersionId = useBuilderStore((state) => state.draftVersionId);
  const dirty = useBuilderStore((state) => state.dirty);
  const isSaving = useBuilderStore((state) => state.isSaving);
  const lastSavedAt = useBuilderStore((state) => state.lastSavedAt);
  const formStatus = useBuilderStore((state) => state.formStatus);
  const setFormStatus = useBuilderStore((state) => state.setFormStatus);
  const setDraftVersionId = useBuilderStore((state) => state.setDraftVersionId);
  const schema = useBuilderStore((state) => state.schema);

  const isInitialMetadata = useRef(true);

  const metadataMutation = useMutation({
    mutationFn: (payload: { title: string; description: string }) => {
      if (!formId) throw new Error("Form id missing");
      return updateFormMetadata(formId, { title: payload.title, description: payload.description });
    },
    onSuccess(data) {
      setFormTitle(data.form.title);
      setFormDescription(data.form.description ?? "");
      void queryClient.invalidateQueries({ queryKey: ["form", formId] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("Form ID is required");

      const { createDraftVersion } = await import("../lib/api-client");

      // Always create a fresh version with current schema
      // This ensures we publish exactly what's in the editor right now
      console.log("[Publish] Creating fresh version with current schema");
      const newVersion = await createDraftVersion(formId, schema);

      // Now publish the newly created version
      return publishDraftVersion(newVersion.id);
    },
    onSuccess(version) {
      // Update store with new version info
      setFormStatus("published");
      setDraftVersionId(version.id);
      setPublishedVersion(version);

      // Clear dirty state - schema is now saved
      const { markSaved, loadSchema } = useBuilderStore.getState();
      loadSchema({
        id: version.id,
        formId: version.formId,
        versionId: version.id,
        schema: version.schema,
        logic: version.logic,
        settings: {},
        updatedAt: version.updatedAt,
      });
      markSaved();

      void queryClient.invalidateQueries({ queryKey: ["form", formId] });

      // Auto-create public share link
      if (formId) {
        createShareMutation.mutate();
      } else {
        // Show modal even if share creation fails
        setShowShareModal(true);
      }
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to publish form.",
      });
    },
  });

  const createShareMutation = useMutation({
    mutationFn: async () => {
      if (!formId) throw new Error("Form id missing");
      const { updateFormShareRequest } = await import("../lib/api-client");

      // Check if a public share already exists for this form
      const existingShares = await listFormShares(formId);
      const publicShare = existingShares.shares.find(s => s.shareType === "public");

      if (publicShare) {
        // If existing share has a formVersionId, clear it so it serves latest version
        if (publicShare.formVersionId) {
          const updated = await updateFormShareRequest(formId, publicShare.id, {
            formVersionId: null, // Clear to serve latest published version
          });
          return { share: updated.share, reused: true };
        }
        // Already configured correctly - just reuse
        return { share: publicShare, reused: true };
      }

      // No existing public share, create new one WITHOUT formVersionId
      // This way the share always serves the latest published version
      const result = await createFormShareRequest(formId, {
        shareType: "public",
        // Don't pass formVersionId - backend will serve latest published version
      });
      return { share: result.share, reused: false };
    },
    onSuccess(data) {
      const url = `${window.location.origin}/share/${data.share.token}`;
      setShareUrl(url);
      setShowShareModal(true);

      // Copy to clipboard
      void navigator.clipboard.writeText(url).then(() => {
        setToast({
          type: "success",
          message: data.reused
            ? "Form published! Share link copied to clipboard."
            : "Form published! Share link created and copied to clipboard.",
        });
        return;
      }).catch(() => {
        setToast({
          type: "success",
          message: data.reused ? "Form published!" : "Form published! Share link created.",
        });
      });
    },
    onError(error: unknown) {
      // Show modal even if share creation fails
      setShowShareModal(true);
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create share link.",
      });
    },
  });

  useEffect(() => {
    if (!formId) return;
    if (isInitialMetadata.current) {
      isInitialMetadata.current = false;
      return;
    }
    const timeout = setTimeout(() => {
      void metadataMutation.mutateAsync({ title: formTitle, description: formDescription });
    }, 800);
    return () => clearTimeout(timeout);
  }, [formTitle, formDescription, formId, metadataMutation]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showShareModal) {
        setShowShareModal(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showShareModal]);

  // Focus management for modal
  useEffect(() => {
    if (showShareModal && modalRef.current) {
      // Focus the modal when it opens
      const firstFocusable = modalRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [showShareModal]);

  const statusLabel = useMemo(() => {
    if (publishMutation.isPending || publishMutation.isSuccess) {
      return publishMutation.isPending ? "Publishing…" : "Published";
    }
    if (isSaving) return "Saving…";
    return formatTimestamp(lastSavedAt);
  }, [isSaving, lastSavedAt, publishMutation.isPending, publishMutation.isSuccess]);

  const connectionLabel = useMemo(() => {
    switch (connectionStatus) {
      case "connected":
        return "Live collaboration";
      case "connecting":
        return "Connecting…";
      case "disconnected":
        return "Offline mode";
      default:
        return "Collaboration idle";
    }
  }, [connectionStatus]);

  return (
    <header
      className="builder-header"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem 1.75rem",
        borderBottom: "1px solid var(--color-border)",
        background: "var(--color-surface)",
        position: "sticky",
        top: 0,
        zIndex: 5,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "1.25rem", flex: 1, minWidth: 0 }}>
        <div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="button ghost"
            style={{ padding: "0.4rem 0.8rem", fontSize: "0.8rem" }}
          >
            Back
          </button>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            value={formTitle}
            onChange={(event) => setFormTitle(event.target.value)}
            placeholder="Form title"
            className="builder-title"
            style={{
              width: "100%",
              border: "none",
              fontSize: "1.3rem",
              fontWeight: "var(--font-weight-heading)",
              outline: "none",
              background: "transparent",
            }}
          />
          <textarea
            value={formDescription}
            onChange={(event) => setFormDescription(event.target.value)}
            placeholder="Describe the purpose of this form"
            rows={2}
            className="desktop-only"
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              resize: "vertical",
              fontSize: "0.9rem",
              color: "var(--color-text-muted)",
              background: "transparent",
            }}
          />
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.35rem", flexWrap: "wrap" }}>
            <span className="pill">{formStatus.toUpperCase()}</span>
            <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{statusLabel}</span>
            {dirty && !isSaving && (
              <span style={{ fontSize: "0.8rem", color: "var(--color-critical)", fontWeight: 500 }}>
                Unsaved changes
              </span>
            )}
            {connectionStatus === "disconnected" && (
              <span style={{ fontSize: "0.8rem", color: "var(--color-critical)", fontWeight: 500 }}>
                Offline edits queued
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", minWidth: 0 }} className="desktop-only">
          <div style={{ display: "flex", alignItems: "center" }}>
            {collaborators.slice(0, 3).map((collaborator, index) => (
              <div
                key={collaborator.clientId}
                title={collaborator.displayName}
                style={{
                  width: "1.6rem",
                  height: "1.6rem",
                  borderRadius: "999px",
                  border: "2px solid var(--color-surface)",
                  background: collaborator.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  marginLeft: index === 0 ? 0 : -12,
                  boxShadow: "var(--shadow-sm)",
                  textTransform: "uppercase",
                }}
              >
                {initialsForName(collaborator.displayName)}
              </div>
            ))}
            {collaborators.length > 3 ? (
              <span className="pill" style={{ marginLeft: 4 }}>
                +{collaborators.length - 3}
              </span>
            ) : null}
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{connectionLabel}</span>
        </div>

        {/* Desktop Actions */}
        <div className="desktop-only" style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="button ghost"
            onClick={() => {
              if (teamId) {
                navigate(`/teams/${teamId}/themes`);
              }
            }}
          >
            Theme studio
          </button>
          <button
            type="button"
            className="button ghost"
            onClick={() => {
              if (teamId && formId) {
                navigate(`/teams/${teamId}/forms/${formId}/analytics`);
              }
            }}
          >
            Analytics
          </button>
          <button
            type="button"
            className="button ghost"
            onClick={() => {
              if (teamId && formId) {
                navigate(`/teams/${teamId}/forms/${formId}/preview`);
              }
            }}
          >
            Preview
          </button>
          {formStatus === "published" && (
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                if (teamId && formId) {
                  navigate(`/teams/${teamId}/forms/${formId}/sharing`);
                }
              }}
            >
              🔗 Share
            </button>
          )}
        </div>

        {/* Mobile Actions Menu Button */}
        <div className="mobile-only" style={{ position: "relative" }}>
          <button
            type="button"
            className="button ghost"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            style={{ padding: "0.5rem", minWidth: "auto" }}
          >
            <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>⋮</span>
          </button>

          {showMobileMenu && (
            <>
              <div
                style={{ position: "fixed", inset: 0, zIndex: 99 }}
                onClick={() => setShowMobileMenu(false)}
              />
              <div className="mobile-actions-menu">
                <button
                  type="button"
                  className="button ghost"
                  style={{ width: "100%", justifyContent: "flex-start", marginBottom: "0.25rem" }}
                  onClick={() => {
                    setShowMobileMenu(false);
                    if (teamId) navigate(`/teams/${teamId}/themes`);
                  }}
                >
                  🎨 Theme studio
                </button>
                <button
                  type="button"
                  className="button ghost"
                  style={{ width: "100%", justifyContent: "flex-start", marginBottom: "0.25rem" }}
                  onClick={() => {
                    setShowMobileMenu(false);
                    if (teamId && formId) navigate(`/teams/${teamId}/forms/${formId}/analytics`);
                  }}
                >
                  📊 Analytics
                </button>
                <button
                  type="button"
                  className="button ghost"
                  style={{ width: "100%", justifyContent: "flex-start", marginBottom: "0.25rem" }}
                  onClick={() => {
                    setShowMobileMenu(false);
                    if (teamId && formId) navigate(`/teams/${teamId}/forms/${formId}/preview`);
                  }}
                >
                  👁️ Preview
                </button>
                {formStatus === "published" && (
                  <button
                    type="button"
                    className="button ghost"
                    style={{ width: "100%", justifyContent: "flex-start" }}
                    onClick={() => {
                      setShowMobileMenu(false);
                      if (teamId && formId) navigate(`/teams/${teamId}/forms/${formId}/sharing`);
                    }}
                  >
                    🔗 Share
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="button primary"
          disabled={!draftVersionId || publishMutation.isPending}
          onClick={() => {
            // Validate form before publishing
            const validation = validateFormSchema(schema);
            if (!validation.valid) {
              const errorMessages = validation.issues
                .filter((issue) => issue.severity === "error")
                .map((issue) => issue.message)
                .join(", ");
              setToast({
                type: "error",
                message: `Form validation failed: ${errorMessages}`,
              });
              return;
            }

            // Check if form has at least one field
            if (schema.fields.length === 0) {
              setToast({
                type: "error",
                message: "Cannot publish form with no fields. Please add at least one field.",
              });
              return;
            }

            // Check if form title is set
            if (!formTitle || formTitle.trim().length === 0) {
              setToast({
                type: "error",
                message: "Please set a form title before publishing.",
              });
              return;
            }

            void publishMutation.mutateAsync();
          }}
        >
          {publishMutation.isPending ? "Publishing…" : "Publish"}
        </button>

        {/* User Menu */}
        <div style={{ position: "relative" }}>
          <button
            type="button"
            className="button ghost"
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              width: "2.25rem",
              height: "2.25rem",
              borderRadius: "50%",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--color-primary)",
              color: "white",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
            title={user?.email}
          >
            {user?.email?.charAt(0).toUpperCase() || "U"}
          </button>

          {showUserMenu && (
            <>
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9,
                }}
                onClick={() => setShowUserMenu(false)}
              />
              <div
                className="animate-fade-in"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 0.5rem)",
                  minWidth: "200px",
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-base)",
                  boxShadow: "var(--shadow-lg)",
                  padding: "0.5rem",
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    padding: "0.75rem 1rem",
                    borderBottom: "1px solid var(--color-border)",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>{user?.email}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "0.25rem" }}>
                    {user?.roles.join(", ") || "User"}
                  </div>
                </div>

                {/* Theme Toggle */}
                <div
                  style={{
                    padding: "0.5rem 1rem",
                    borderBottom: "1px solid var(--color-border)",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>Theme</div>
                  <ThemeToggle />
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  style={{
                    width: "100%",
                    padding: "0.625rem 1rem",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    borderRadius: "calc(var(--radius-base) - 2px)",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: "var(--color-critical)",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--color-bg)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <>
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowShareModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-modal-title"
          >
            <div
              ref={modalRef}
              className="modal-content surface"
              style={{
                background: "var(--color-surface)",
                padding: "2rem",
                borderRadius: "12px",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            >
              <h2 id="share-modal-title" style={{ marginBottom: "0.5rem" }}>
                Form Published Successfully!
              </h2>
              <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
                Your form has been published. Create a share link to start collecting responses.
              </p>

              {shareUrl ? (
                <div style={{ marginBottom: "1.5rem" }}>
                  <label
                    htmlFor="share-url"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                    }}
                  >
                    Share Link
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      id="share-url"
                      type="text"
                      value={shareUrl}
                      readOnly
                      style={{
                        flex: 1,
                        padding: "0.75rem",
                        border: "1px solid var(--color-border)",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        background: "var(--color-bg)",
                      }}
                    />
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => {
                        void navigator.clipboard.writeText(shareUrl).then(() => {
                          setToast({ type: "success", message: "Link copied to clipboard!" });
                          return;
                        });
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  <button
                    type="button"
                    className="button primary"
                    onClick={() => {
                      if (formId && publishedVersion) {
                        createShareMutation.mutate();
                      }
                    }}
                    disabled={createShareMutation.isPending}
                  >
                    {createShareMutation.isPending ? "Creating..." : "Create Share Link"}
                  </button>
                </div>
              )}

              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => {
                    if (teamId && formId) {
                      navigate(`/teams/${teamId}/forms/${formId}/sharing`);
                    }
                    setShowShareModal(false);
                  }}
                >
                  Manage Sharing
                </button>
                <button
                  type="button"
                  className="button primary"
                  onClick={() => setShowShareModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            background: toast.type === "success" ? "var(--color-success)" : "var(--color-critical)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "var(--radius-base)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 1001,
          }}
        >
          {toast.message}
        </div>
      )}
    </header>
  );
}

function initialsForName(value: string): string {
  if (!value) return "?";
  const userPart = value.includes("@") ? value.split("@")[0] : value;
  const segments = userPart.split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (segments.length === 0) {
    return userPart.slice(0, 2).toUpperCase();
  }
  const letters = segments.slice(0, 2).map((segment) => segment.charAt(0).toUpperCase());
  return letters.join("") || userPart.slice(0, 2).toUpperCase();
}

/**
 * Theme toggle component for switching between Light/Dark/System modes
 */
function ThemeToggle(): JSX.Element {
  const { mode, setMode } = useTheme();

  const options: { value: ThemeMode; label: string; icon: string }[] = [
    { value: "light", label: "Light", icon: "☀️" },
    { value: "dark", label: "Dark", icon: "🌙" },
    { value: "system", label: "System", icon: "💻" },
  ];

  return (
    <div style={{ display: "flex", gap: "0.25rem" }}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => setMode(option.value)}
          style={{
            flex: 1,
            padding: "0.4rem 0.5rem",
            fontSize: "0.75rem",
            background: mode === option.value ? "var(--color-primary-soft)" : "transparent",
            color: mode === option.value ? "var(--color-primary)" : "var(--color-text)",
            border: mode === option.value ? "1px solid var(--color-primary)" : "1px solid var(--color-border)",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.15s",
          }}
          title={option.label}
        >
          {option.icon}
        </button>
      ))}
    </div>
  );
}

export default BuilderTopBar;
